import React, { useMemo } from "react";
import { merge, $ } from "glamor";
import { theme } from "styles/theme";
import { formatMessage } from "lib/globalize-util";
import { BrandField } from "./brand-field";
import { DATATEST } from "constants/integration";
import { ICollectionSwatch, SwatchType } from "constants/color-swatches";
import { ITileSettings, ITileSettingsValue } from "components/product-collection/manage-tile-view/types";
import classNames from "classnames";

interface IProps {
    productName: string;
    styleNumber: string;
    color?: string;
    wholesalePrice?: string;
    retailPrice?: string;
    brandId: string;
    productId: string;
    router?: any;
    brandEntities: any;
    colorSwatch?: ICollectionSwatch;
    hidePrices?: boolean;
    showBrandIcon?: boolean;
    brandNameOverride?: string;
    tileSettings?: ITileSettings;
    tileSettingsValue?: ITileSettingsValue;
    onClickProductName?: (productId: string) => void;
    getProductNameLink?: (productId: string) => string;
}

export interface IProductDisplayFieldsHeaderProps {
    productId: string | null;
    productName: string;
    tileSettings?: ITileSettings;
    onClickProductName?: (productId: string) => void;
    getProductNameLink?: (productId: string) => string;
}

export const ProductDisplayFieldsHeader: React.FC<IProductDisplayFieldsHeaderProps> = ({
    productId,
    productName,
    onClickProductName,
    tileSettings,
    getProductNameLink,
}) => {
    let header: any = "";

    const handleClickProductName = React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            event.preventDefault();
            event.stopPropagation();
            onClickProductName?.(productId);
        },
        [onClickProductName, productId],
    );

    const link = useMemo(() => getProductNameLink?.(productId) ?? "#", [getProductNameLink, productId]);

    if (productName) {
        if (onClickProductName && productId) {
            header = (
                <a
                    data-test={DATATEST.ASSORTMENTS.DETAILS_PRODUCT_NAME}
                    href={link}
                    className="productName"
                    onClick={handleClickProductName}
                >
                    {productName}
                </a>
            );
        } else {
            header = (
                <div data-test={DATATEST.ASSORTMENTS.SHOP_CARD_PRODUCT_NAME} className="productName">
                    {productName}
                </div>
            );
        }
    }

    if (tileSettings && productName === "") {
        header = <div className="productName" />;
    }

    return header;
};

export const ProductDisplayFields: React.FunctionComponent<IProps> = ({
    productName,
    styleNumber,
    color,
    wholesalePrice,
    retailPrice,
    brandId,
    productId,
    router,
    onClickProductName,
    colorSwatch,
    brandEntities,
    hidePrices,
    showBrandIcon,
    brandNameOverride,
    tileSettings,
    tileSettingsValue,
    getProductNameLink,
}) => {
    const styles = React.useMemo(() => {
        return merge(
            {
                flex: "1",
                display: tileSettings ? "block" : "inline-grid",
            },
            $(" .moreLines", {
                height: "16px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
            }),
            $(" > a", {
                textDecoration: "underline",
            }),
            $(" .brandName a", {
                textDecoration: "underline",
            }),
            $(" .productName", {
                lineHeight: "28px",
                fontSize: "24px",
                fontWeight: "bold",
                color: theme.palette.darkestGrey,
                paddingBottom: "4px",
                letterSpacing: "0.3px",
            }),
            $(" .styleNumber", {
                color: theme.palette.darkGrey,
                fontSize: "14px",
                lineHeight: "16px",
                paddingBottom: "16px",
            }),
            $(" .price", {
                color: theme.palette.darkGrey,
                lineHeight: "16px",
                fontSize: "14px",
                paddingBottom: "4px",
            }),
            $(" .price strong", {
                fontSize: "16px",
                fontWeight: "bold",
                color: theme.palette.darkestGrey,
            }),
            $(" .color", {
                height: "48px",
                lineHeight: "48px",
                paddingLeft: "12px",
                fontSize: "14px",
            }),
            $(" .colorSwatch", {
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                border: `solid 1px ${theme.palette.swatchBorderColor}`,
                marginBottom: "10px",
                display: "inline-block",
                position: "relative",
            }),
            $(" .colorContainer", {
                display: "flex",
                paddingTop: "12px",
            }),
        );
    }, [tileSettings]);

    if (color === undefined) {
        color = colorSwatch?.color;
    }

    const swatchStyles = useMemo(() => {
        switch (colorSwatch?.swatch_type) {
            case SwatchType.HEX:
                return { backgroundColor: colorSwatch.value ? `#${colorSwatch.value}` : colorSwatch.color }; // weird color usage (legacy support)
            case SwatchType.FILE:
                return {
                    backgroundImage: `url(//cdn1.nuorder.com/brand_color_swatch/${colorSwatch.value}.jpg)`,
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                };
            default:
                return { backgroundColor: theme.palette.swatchBackgroundColor };
        }
    }, [colorSwatch]);

    const allTileSettingsUndefinedExceptFourth =
        tileSettings &&
        Object.keys(tileSettings)
            ?.filter?.((line) => line !== "fourthLine")
            ?.every?.((line) => tileSettingsValue?.[tileSettings?.[line]] === undefined);

    return (
        <div {...styles}>
            <BrandField
                tileSettings={tileSettings}
                brandEntities={brandEntities}
                brandNameOverride={brandNameOverride}
                brandId={brandId}
                showBrandIcon={showBrandIcon}
            />

            <ProductDisplayFieldsHeader
                productId={productId}
                productName={productName}
                tileSettings={tileSettings}
                onClickProductName={onClickProductName}
                getProductNameLink={getProductNameLink}
            />

            {(!tileSettings || styleNumber !== undefined) && (
                <div data-test={DATATEST.ASSORTMENTS.DETAILS_STYLE_NUMBER} className="styleNumber">
                    {styleNumber}
                </div>
            )}
            {!hidePrices && (
                <React.Fragment>
                    {wholesalePrice && (
                        <div className="price">
                            {formatMessage("Cost:")}
                            <strong>{` ${wholesalePrice} `}</strong>
                            {formatMessage("/ ea")}
                        </div>
                    )}
                    {retailPrice && (
                        <div className="price">
                            {formatMessage("Retail:")}
                            <strong>{` ${retailPrice} `}</strong>
                            {formatMessage("/ ea")}
                        </div>
                    )}
                </React.Fragment>
            )}
            <div
                className={classNames("colorContainer", {
                    thereAreNotLines: tileSettings && allTileSettingsUndefinedExceptFourth,
                })}
            >
                {(!tileSettings || tileSettings.showColorSwatch) && (
                    <div
                        key={`${productId}-${colorSwatch?.value ? `#${colorSwatch.value}` : colorSwatch?.color}`}
                        className="colorSwatch"
                        style={swatchStyles}
                        data-test={`swatch-${productId}`}
                    />
                )}
                {tileSettings && tileSettingsValue[tileSettings.fourthLine] !== undefined && (
                    <div
                        data-test={DATATEST.ASSORTMENTS.DETAILS_PRODUCT_COLOR}
                        className={classNames("color", {
                            swatchColorActive: tileSettings.showColorSwatch,
                        })}
                    >
                        {tileSettingsValue[tileSettings.fourthLine]}
                    </div>
                )}
                {!tileSettings && (
                    <div data-test={DATATEST.ASSORTMENTS.DETAILS_PRODUCT_COLOR} className="color">
                        {color}
                    </div>
                )}
            </div>
            {tileSettings?.fifthLine && <div className="moreLines">{tileSettingsValue[tileSettings.fifthLine]}</div>}
            {tileSettings?.sixthLine && <div className="moreLines">{tileSettingsValue[tileSettings.sixthLine]}</div>}
            {tileSettings?.seventhLine && (
                <div className="moreLines">{tileSettingsValue[tileSettings.seventhLine]}</div>
            )}
            {tileSettings?.eighthLine && <div className="moreLines">{tileSettingsValue[tileSettings.eighthLine]}</div>}
        </div>
    );
};
